import * as Sentry from '@sentry/react';
import { Modal, Steps, Alert, message, Button, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { jt, t } from 'ttag';

import Loading from 'common/components/Loading';
import useConfig from 'common/hooks/useConfig';
import { SALES_EMAIL } from 'common/services/support/types';
import { getContract, getLicenseType } from 'common/state/organization';
import { getIsStaff } from 'common/state/user';

import ExpiredContractImage from '@assets/images/illustrations/expired-contract.svg';
import { STATUS_EXPIRED } from '@domains/Organization/contracts-service';
import { LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT } from '@domains/Organization/service';

import Content, { SOURCE_DUPLICATE } from './Content';
import {
  redeemCredit as redeemCreditFn,
  getCredits as getCreditsFn,
  getBlueprints as getBlueprintsFn,
  CREDITS_STATUS_EXPIRED,
  CREDITS_STATUS_AVAILABLE,
} from './credit-service';
import Credits from './Credits';
import Details from './Details';
import DuplicationChecklist from './Duplication/Checklist';
import { buildModules, defaultSelection, toDuplicationConfig } from './Duplication/modules';
import { effectiveSelection, makeIsFeatureEnabled } from './Duplication/selection';
import { useCreateDuplicateEvent } from './queries';

const ExpiredContractWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FORMAT_DATE = 'YYYY-MM-DD';
const DEFAULT_PRIMARY_LANGUAGE = 'en';

const STEP_CREDIT = 0;
const STEP_CONTENT = 1;
const STEP_DETAILS = 2;

const RESERVED_WORDS = [
  'reg',
  'registration',
  'register',
  'page',
  'page-builder',
  'pagebuilder',
  'website',
  'web',
  'websitebuilder',
  'website-builder',
];

const ERROR_MESSAGES_BY_CODE = () => ({
  '292a9a29-c548-4ea2-bcfe-3b0c130c5b52': t`The Event Code is already in use. Please enter a different code.`,
  '64ac0696-430f-463d-ae4a-583a37c6832e': t`Event Duration cannot exceed more than 7 days`,
  '99ca0907-59cd-462e-89b5-031642339521': t`Please enter valid information for Event.`,
  credit_id_is_required: t`Please select a credit to duplicate this event.`,
});

const initialState = {
  createMode: null,
  currentStep: STEP_CREDIT,
  credit: null,
  source: null,
  name: '',
  dates: [],
  code: '',
  eventFormat: 'onsite',
  eventType: '',
  primaryLanguage: DEFAULT_PRIMARY_LANGUAGE,
  secondaryLanguage: '',
  secondaryLanguageEnabled: false,
  sourceEvent: null,
  selection: null,
  loading: false,
  error: null,
  isReserved: false,
};

const ACTION_SET_CREATE_MODE = 'SET_CREATE_MODE';
const ACTION_SET_STEP = 'SET_STEP';
const ACTION_SET_CREDIT = 'SET_CREDIT';
const ACTION_SET_LOADING = 'SET_LOADING';
const ACTION_SET_SOURCE = 'SET_SOURCE';
const ACTION_SET_NAME = 'SET_NAME';
const ACTION_SET_DATES = 'SET_DATES';
const ACTION_SET_CODE = 'SET_CODE';
const ACTION_SET_EVENT_FORMAT = 'SET_EVENT_FORMAT';
const ACTION_SET_EVENT_TYPE = 'SET_EVENT_TYPE';
const ACTION_SET_PRIMARY_LANGUAGE = 'SET_PRIMARY_LANGUAGE';
const ACTION_SET_SECONDARY_LANGUAGE = 'SET_SECONDARY_LANGUAGE';
const ACTION_SET_SECONDARY_LANGUAGE_ENABLED = 'SET_SECONDARY_LANGUAGE_ENABLED';
const ACTION_SET_SOURCE_EVENT = 'SET_EVENT_ID';
const ACTION_SET_SELECTION = 'SET_SELECTION';
const ACTION_SET_ERROR = 'SET_ERROR';
const ACTION_RESET = 'RESET';

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_SET_CREATE_MODE: {
      const newStep = action.payload === LICENSE_TYPE_EVENT_CREDIT ? STEP_CREDIT : STEP_CONTENT;
      return {
        ...state,
        createMode: action.payload,
        currentStep: newStep,
        error: null,
      };
    }
    case ACTION_SET_STEP: {
      return { ...state, currentStep: action.payload };
    }
    case ACTION_SET_CREDIT: {
      return {
        ...state,
        credit: action.payload,
      };
    }
    case ACTION_SET_SOURCE: {
      return { ...state, source: action.payload };
    }
    case ACTION_SET_SOURCE_EVENT: {
      return {
        ...state,
        sourceEvent: action.payload,
        selection: null,
      };
    }
    case ACTION_SET_SELECTION: {
      return { ...state, selection: action.payload };
    }
    case ACTION_SET_NAME: {
      return { ...state, name: action.payload };
    }
    case ACTION_SET_CODE: {
      return {
        ...state,
        code: action.payload,
        isReserved: RESERVED_WORDS.includes(action.payload?.toLowerCase()),
      };
    }
    case ACTION_SET_DATES: {
      return { ...state, dates: action.payload };
    }
    case ACTION_SET_EVENT_FORMAT: {
      return { ...state, eventFormat: action.payload };
    }
    case ACTION_SET_EVENT_TYPE: {
      return { ...state, eventType: action.payload };
    }
    case ACTION_SET_PRIMARY_LANGUAGE: {
      return { ...state, primaryLanguage: action.payload };
    }
    case ACTION_SET_SECONDARY_LANGUAGE: {
      return { ...state, secondaryLanguage: action.payload };
    }
    case ACTION_SET_SECONDARY_LANGUAGE_ENABLED: {
      return { ...state, secondaryLanguageEnabled: action.payload };
    }
    case ACTION_SET_LOADING: {
      return { ...state, loading: action.payload };
    }
    case ACTION_SET_ERROR: {
      return { ...state, error: action.payload };
    }
    case ACTION_RESET: {
      return { ...initialState };
    }
    default:
      return { ...state };
  }
};

const Switcher = ({ children, index }) => children[index];

const useCredits = orgId => {
  const isStaff = useSelector(getIsStaff);
  const [credits, setCredits] = useState({
    [CREDITS_STATUS_AVAILABLE]: [],
    [CREDITS_STATUS_EXPIRED]: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function effect() {
      try {
        let res = await getCreditsFn(orgId);

        let blueprints;
        if (isStaff) {
          blueprints = await getBlueprintsFn();
        }

        res = res
          .map(credit => {
            const obj = { ...credit };

            if (blueprints) {
              obj.packageName = blueprints.find(c => c.id === credit.blueprintId)?.name;
            }

            return obj;
          })
          .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))
          .reduce(
            (acc, cur) => {
              const k = cur.isExpired ? CREDITS_STATUS_EXPIRED : CREDITS_STATUS_AVAILABLE;
              acc[k].push(cur);

              return acc;
            },
            { [CREDITS_STATUS_AVAILABLE]: [], [CREDITS_STATUS_EXPIRED]: [] },
          );

        setCredits(res);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    effect();
  }, [orgId, isStaff]);

  return { credits, loading, error };
};

const CreateModeSwitcher = ({ currentMode, onChange, hasCredits }) => {
  const licenseType = useSelector(getLicenseType);

  if (licenseType === LICENSE_TYPE_USER_CREDIT) {
    if (currentMode === LICENSE_TYPE_USER_CREDIT && hasCredits) {
      const switchModeLink = (
        <Typography.Link key="switch-link" onClick={() => onChange(LICENSE_TYPE_EVENT_CREDIT)}>
          {t`Use Single Event Credit`}
        </Typography.Link>
      );
      return (
        <Alert
          showIcon
          type="info"
          title={jt`You have unused Single Event Credit available outside your Annual Subscription License. Events created with this credit will have a limited attendee capacity, and People Credit won’t be applicable. ${switchModeLink}.`}
        />
      );
    }
  }

  return null;
};

CreateModeSwitcher.propTypes = {
  currentMode: PropTypes.oneOf([LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT]).isRequired,
  onChange: PropTypes.func.isRequired,
  hasCredits: PropTypes.bool.isRequired,
};

const ExpiredContractContent = ({ onUseSingleEventCredit, hasCredits }) => {
  const salesEmailLink = (
    <Typography.Link
      key="salesEmail"
      target="_blank"
      rel="noreferrer noopener"
      href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Expired Annual Contract')}`}
    >
      {t`contact our sales team`}
    </Typography.Link>
  );

  const useSingleEventCreditLink = (
    <Typography.Link key="useSingleEventCredit" onClick={onUseSingleEventCredit}>
      {t`Use Single Event Credit.`}
    </Typography.Link>
  );

  return (
    <ExpiredContractWrapper>
      <div style={{ marginBottom: 16 }}>
        <img src={ExpiredContractImage} alt={t`Expired Contract`} width={167} height={100} />
      </div>
      <Typography.Title level={4}>{t`Your Annual Contract has Expired`}</Typography.Title>
      <Typography.Paragraph>
        {t`You can no longer create new subscription events.`}
        <br />
        {jt`Please ${salesEmailLink} to renew your license.`}
      </Typography.Paragraph>
      {hasCredits && (
        <Alert
          title={jt`You have unused Single Event Credit available outside your Annual Subscription License. Events created with this credit will have a limited attendee capacity, and People Credit won't be applicable. ${useSingleEventCreditLink}`}
          type="info"
          showIcon
        />
      )}
    </ExpiredContractWrapper>
  );
};

ExpiredContractContent.propTypes = {
  onUseSingleEventCredit: PropTypes.func.isRequired,
  hasCredits: PropTypes.bool.isRequired,
};

const CreateModal = ({ open, onClose, orgId, navigateToEvent }) => {
  const licenseType = useSelector(getLicenseType);
  const contract = useSelector(getContract);

  const isExpired = licenseType === LICENSE_TYPE_USER_CREDIT && contract?.status === STATUS_EXPIRED;
  const initialMode = isExpired ? null : licenseType;
  const initialStep = initialMode === LICENSE_TYPE_EVENT_CREDIT ? STEP_CREDIT : STEP_CONTENT;

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    createMode: initialMode,
    currentStep: initialStep,
  });

  const { credits, loading, error } = useCredits(orgId);

  const isDuplication = state.source === SOURCE_DUPLICATE;

  const sourceEventId = isDuplication ? state.sourceEvent?.id : undefined;
  const {
    isConfigEnabled: isSourceConfigEnabled,
    loading: configLoading,
    isError: configErrored,
  } = useConfig(sourceEventId);
  // Without a source event `useConfig` falls back to the (absent) current event,
  // so scope both gates to the duplication path.
  const isSourceConfigLoading = !!sourceEventId && configLoading;
  const isSourceConfigError = !!sourceEventId && configErrored;

  // A module is duplicable only if the source event has it AND the chosen credit
  // licenses it on the new event.
  const isConfigEnabled = useMemo(() => {
    const creditEnabled = makeIsFeatureEnabled(state.credit);
    return key => isSourceConfigEnabled(key) && creditEnabled(key);
  }, [isSourceConfigEnabled, state.credit]);
  const duplicateMutation = useCreateDuplicateEvent(orgId);

  // Show expired contract view when contract is expired and user hasn't chosen single event credit yet
  const showExpiredContractView = isExpired && state.createMode === null;

  const isBackEnabled = useMemo(
    () =>
      // do not show back btn if it's first step for 'event credit' mode
      (state.createMode === LICENSE_TYPE_EVENT_CREDIT && state.currentStep !== STEP_CREDIT) ||
      // do not show back btn if it's first step for 'user credit' mode
      (state.createMode === LICENSE_TYPE_USER_CREDIT && state.currentStep !== STEP_CONTENT),
    [state],
  );

  const isContinueDisabled = useMemo(() => {
    if (state.currentStep === STEP_CREDIT) {
      return !state.credit;
    }
    if (state.currentStep === STEP_CONTENT) {
      return (
        !state.source ||
        (isDuplication &&
          // Wait for the source config so the checklist reflects real availability
          // before the user can proceed and submit an optimistic all-true selection.
          // Without it (fetch failed) every module reads as available, so block too.
          (!state.sourceEvent?.id || isSourceConfigLoading || isSourceConfigError))
      );
    }
    return (
      !state.name ||
      !state.dates?.[0] ||
      !state.dates?.[1] ||
      !state.code ||
      state.isReserved ||
      !state.eventFormat ||
      !state.eventType ||
      !state.primaryLanguage ||
      (state.secondaryLanguageEnabled &&
        (!state.secondaryLanguage || state.secondaryLanguage === state.primaryLanguage))
    );
  }, [state, isDuplication, isSourceConfigLoading, isSourceConfigError]);

  const handleSubmit = async () => {
    if (state.currentStep === STEP_CREDIT) {
      dispatch({ type: ACTION_SET_STEP, payload: STEP_CONTENT });
      return;
    }
    if (state.currentStep === STEP_CONTENT) {
      dispatch({ type: ACTION_SET_STEP, payload: STEP_DETAILS });
      return;
    }

    if (state.error) {
      dispatch({ type: ACTION_SET_ERROR, payload: null });
    }

    dispatch({ type: ACTION_SET_LOADING, payload: true });
    const [startDate, endDate] = state.dates;
    const payload = { ...state };
    if (state.createMode === LICENSE_TYPE_USER_CREDIT) {
      delete payload.credit;
    }
    // Rewrite form dates to follow api format
    payload.startDate = startDate.format(FORMAT_DATE);
    payload.endDate = endDate.format(FORMAT_DATE);

    const tadaEmoji = '\uD83C\uDF89';

    try {
      if (isDuplication) {
        const modules = buildModules();
        const selection = state.selection || defaultSelection(modules);
        const duplicationConfig = toDuplicationConfig(
          effectiveSelection(modules, selection, isConfigEnabled),
        );
        const res = await duplicateMutation.mutateAsync({ data: payload, duplicationConfig });
        if (res?.id) {
          message.success(t`Congratulations. Your event has been created. ${tadaEmoji}`);
          navigateToEvent(res.id);
        }
        return;
      }

      const res = await redeemCreditFn(payload, orgId);
      if (res && res.eventId) {
        message.success(t`Congratulations. Your event has been created. ${tadaEmoji}`);
        navigateToEvent(res.eventId);
      }
    } catch (err) {
      const firstError = err?.errors?.[0];
      const mappedMessage = ERROR_MESSAGES_BY_CODE()[firstError?.code];
      let errorPayload = mappedMessage;
      if (isDuplication) {
        // Expected validation rejections (4xx) are shown to the user, not sent to
        // Sentry; only genuinely unexpected failures are reported.
        const isValidationError = err?.status >= 400 && err?.status < 500;
        if (!isValidationError) Sentry.captureException(err);
        errorPayload =
          mappedMessage || firstError?.message || firstError?.detail || firstError?.title;
      }
      dispatch({ type: ACTION_SET_ERROR, payload: errorPayload });
      message.error(t`Error Creating Event.`);
    } finally {
      dispatch({ type: ACTION_SET_LOADING, payload: false });
    }
  };

  const handleBack = () => {
    dispatch({ type: ACTION_SET_STEP, payload: state.currentStep - 1 });
  };

  const handleCancel = () => {
    dispatch({ type: ACTION_RESET });
    onClose();
  };

  const handleUseSingleEventCredit = () => {
    dispatch({ type: ACTION_SET_CREATE_MODE, payload: LICENSE_TYPE_EVENT_CREDIT });
  };

  return (
    <Modal
      title={t`Create an Event`}
      open={open}
      width={700}
      destroyOnHidden
      mask={{ closable: false }}
      onCancel={handleCancel}
      footer={
        showExpiredContractView ? (
          <Button key="cancel" onClick={handleCancel}>
            {t`Cancel`}
          </Button>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button onClick={handleCancel}>{t`Cancel`}</Button>
            <Space>
              {isBackEnabled && <Button onClick={handleBack}>{t`Back`}</Button>}
              <Button
                type="primary"
                loading={state.loading}
                disabled={isContinueDisabled}
                onClick={handleSubmit}
              >
                {state.currentStep === STEP_DETAILS ? t`Create Event` : t`Continue`}
              </Button>
            </Space>
          </div>
        )
      }
    >
      {loading && <Loading />}
      {!loading && error && (
        <Alert type="error" title={t`Something went wrong. Please try again.`} />
      )}
      {!loading && !error && showExpiredContractView && (
        <ExpiredContractContent
          onUseSingleEventCredit={handleUseSingleEventCredit}
          hasCredits={credits[CREDITS_STATUS_AVAILABLE]?.length > 0}
        />
      )}
      {!loading && !error && !showExpiredContractView && (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {state.createMode === LICENSE_TYPE_EVENT_CREDIT && (
            <Steps
              size="small"
              current={state.currentStep}
              onChange={current => dispatch({ type: ACTION_SET_STEP, payload: current })}
              items={[
                { title: t`Select Credit` },
                {
                  title: `Content`,
                  disabled: state.currentStep === STEP_DETAILS ? false : isContinueDisabled,
                },
                {
                  title: `Event Details`,
                  disabled: isContinueDisabled,
                },
              ]}
            />
          )}
          {state.error && <Alert title={state.error} type="error" showIcon />}
          <Switcher index={state.currentStep}>
            <Credits
              selected={state.credit?.id}
              credits={credits}
              onSelect={credit => dispatch({ type: ACTION_SET_CREDIT, payload: credit })}
            />
            <Content
              currentMode={state.createMode}
              hasContract={!!contract}
              orgId={orgId}
              setSource={v => dispatch({ type: ACTION_SET_SOURCE, payload: v })}
              setSourceEvent={e => dispatch({ type: ACTION_SET_SOURCE_EVENT, payload: e })}
              sourceEvent={state.sourceEvent}
              source={state.source}
            >
              {isDuplication && state.sourceEvent?.id && (
                <DuplicationChecklist
                  selection={state.selection}
                  onSelectionChange={s => dispatch({ type: ACTION_SET_SELECTION, payload: s })}
                  isConfigEnabled={isConfigEnabled}
                  isLoading={isSourceConfigLoading}
                  hasError={isSourceConfigError}
                />
              )}
            </Content>
            <Details
              currentMode={state.createMode}
              credit={state.credit}
              name={state.name}
              code={state.code}
              dates={state.dates}
              eventFormat={state.eventFormat}
              eventType={state.eventType}
              isReserved={state.isReserved}
              primaryLanguage={state.primaryLanguage}
              secondaryLanguage={state.secondaryLanguage}
              secondaryLanguageEnabled={state.secondaryLanguageEnabled}
              setDates={d => dispatch({ type: ACTION_SET_DATES, payload: d })}
              setCode={c => dispatch({ type: ACTION_SET_CODE, payload: c })}
              setName={n => dispatch({ type: ACTION_SET_NAME, payload: n })}
              setEventFormat={f => dispatch({ type: ACTION_SET_EVENT_FORMAT, payload: f })}
              setEventType={v => dispatch({ type: ACTION_SET_EVENT_TYPE, payload: v })}
              setPrimaryLanguage={v => dispatch({ type: ACTION_SET_PRIMARY_LANGUAGE, payload: v })}
              setSecondaryLanguage={v =>
                dispatch({ type: ACTION_SET_SECONDARY_LANGUAGE, payload: v })
              }
              setSecondaryLanguageEnabled={v =>
                dispatch({ type: ACTION_SET_SECONDARY_LANGUAGE_ENABLED, payload: v })
              }
            />
          </Switcher>
          <CreateModeSwitcher
            currentMode={state.createMode}
            onChange={newMode => dispatch({ type: ACTION_SET_CREATE_MODE, payload: newMode })}
            hasCredits={credits[CREDITS_STATUS_AVAILABLE]?.length > 0}
          />
        </Space>
      )}
    </Modal>
  );
};

CreateModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  orgId: PropTypes.string.isRequired,
  navigateToEvent: PropTypes.func.isRequired,
};

export default CreateModal;
